import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { getMessageFromError } from '@/core';
import { updateAuditorVisitWorkOrder } from '../api/updateAuditorVisitWorkOrder';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { WorkOrderContact } from '@/features/client-workorder/types';
import { getPostOFfice, PostOffice } from '@/common';
import CustomerContactModal from './CustomerContactModal';

type WorkOrderContactDetail = WorkOrderContact & {
  open?: boolean;
};

type AdditionalInfoFormType = {
  title: string;
  address: string;
  postOffice: PostOffice;
  effectiveWorkers: number;
  employees: number;
  agencyWorkers: number;
  students: number;
  locations: boolean;
  multiSite: boolean;
  tempLocations: boolean;
  comment: string;
  contacts: WorkOrderContactDetail[];
};

const Customer = () => {
  const [isAdditionalInfoInEditMode, setIsAdditionalInfoInEditMode] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();
  const [commentShow, setCommentShow] = useState(!!auditorVisit.workOrder?.comment);

  const form = useForm<AdditionalInfoFormType>({
    mode: 'onTouched',
    defaultValues: {
      title: undefined,
      address: undefined,
      postOffice: { id: '' },
      effectiveWorkers: undefined,
      employees: undefined,
      agencyWorkers: undefined,
      students: undefined,
      locations: false,
      multiSite: false,
      tempLocations: false,
      comment: '',
      contacts: [],
    },
  });

  const [contactsData, setContactsData] = useState<WorkOrderContactDetail[]>(
    form.getValues().contacts
  );

  const [showContactModal, setShowContactModal] = useState(false);
  const [indexContact, setIndexContact] = useState(0);

  const watchedFields = form.watch();

  useEffect(() => {
    if (!commentShow && isAdditionalInfoInEditMode) setCommentShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFields]);

  useEffect(() => {
    form.reset({
      title: auditorVisit.workOrder?.title,
      address: auditorVisit.workOrder?.address,
      postOffice: auditorVisit.workOrder?.postOffice,
      effectiveWorkers: auditorVisit.workOrder?.effectiveWorkers,
      employees: auditorVisit.workOrder?.employees,
      agencyWorkers: auditorVisit.workOrder?.agencyWorkers,
      students: auditorVisit.workOrder?.students,
      locations: auditorVisit.workOrder?.locations,
      multiSite: auditorVisit.workOrder?.multiSite,
      tempLocations: auditorVisit.workOrder?.tempLocations,
      comment: auditorVisit.workOrder?.comment,
      contacts: auditorVisit.workOrder?.contacts,
    });
    setContactsData(auditorVisit.workOrder?.contacts || []);
  }, [auditorVisit, form]);

  const updateAuditorVisitWorkOrderMutation = useMutation({
    mutationFn: updateAuditorVisitWorkOrder,
    onSuccess: () => {
      enqueueSnackbar(
        t('Proposal sent successfully.', {
          defaultValue: 'Predlog usprešno poslan.',
        }),
        { variant: 'success' }
      );
      setIsAdditionalInfoInEditMode(false);
      invalidateAuditorVisit();
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const updateContact = (data: Omit<WorkOrderContact, 'id' | 'wo_id' | 'product'>) => {
    form.setValue(`contacts.${indexContact}`, { ...contactsData[indexContact], ...data });
    setContactsData(form.getValues('contacts'));
  };

  const baseSubmit = (data: AdditionalInfoFormType) => {
    if (commentShow && !data.comment) {
      form.setError('comment', { message: 'Comment is required', type: 'required' });
      return;
    }
    if (Object.keys(form.formState.dirtyFields).length === 0) {
      enqueueSnackbar(t('Form was not changed.', { defaultValue: 'Form was not changed.' }), {
        variant: 'info',
      });
      return;
    }

    updateAuditorVisitWorkOrderMutation.mutate({
      visitId: auditorVisit.id,
      data,
    });
  };
  const handleClick = (id: string) => {
    const newList = contactsData.map((contact) => {
      if (id === contact.id) {
        const tmp = {
          ...contact,
          open: !contact.open,
        };
        return tmp;
      }
      return contact;
    });

    setContactsData(newList);
  };
  const renderExpandButton = (contact: WorkOrderContactDetail) => {
    if (form.getValues().contacts.length && contact.open) {
      return <ExpandLessIcon />;
    }
    if (form.getValues().contacts.length && !contact.open) {
      return <ExpandMoreIcon />;
    }
    return null;
  };

  const openModal = () => {
    setShowContactModal(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
        padding={2}
      >
        <Typography variant="h5">
          {t('Additional information', { defaultValue: 'Additional information' })}
        </Typography>
        {auditorVisit.isCurrentAuditorTeamLeader && !isAdditionalInfoInEditMode && (
          <IconButton
            sx={{ ml: '1rem' }}
            onClick={() => setIsAdditionalInfoInEditMode((previous) => !previous)}
          >
            <EditIcon />
          </IconButton>
        )}
      </Box>

      <Box
        component="form"
        noValidate
        sx={{ px: '1rem', mt: '0.5rem', display: 'grid', gap: '1rem' }}
      >
        <CustomerContactModal
          wo_id={auditorVisit?.workOrder?.id}
          isOpen={showContactModal}
          setIsOpen={setShowContactModal}
          contact={contactsData[indexContact]}
          onSubmit={updateContact}
        />
        <List
          sx={{
            display: 'grid',
            'grid-template-columns': 'repeat(2, 1fr)',
          }}
        >
          <ListItem>
            <Controller
              control={form.control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Title', { defaultValue: 'Title' })}
                  fullWidth
                  disabled={!isAdditionalInfoInEditMode}
                  error={!!form.formState.errors.title}
                  helperText={form.formState.errors.title?.message?.toString()}
                />
              )}
            />
          </ListItem>
          <ListItem>
            <Controller
              control={form.control}
              name="address"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Address', { defaultValue: 'Address' })}
                  fullWidth
                  disabled={!isAdditionalInfoInEditMode}
                  error={!!form.formState.errors.address}
                  helperText={form.formState.errors.address?.message?.toString()}
                />
              )}
            />
          </ListItem>
          <ListItem
            sx={{
              '& > div': {
                width: '100%',
              },
            }}
          >
            <AsyncAutocomplete<PostOffice>
              id="post-office"
              label={t('Post office', { defaultValue: 'Post office' })}
              fullWidth
              disabled={!isAdditionalInfoInEditMode}
              getData={async (search) => {
                const response = await getPostOFfice({
                  page: 1,
                  pageSize: 5,
                  search,
                });

                return response.results;
              }}
              getOptionLabel={(option) => `${option.number || ''} ${option.name || ''}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              selectedOption={form.getValues('postOffice')}
              setSelectedOption={(option) => {
                form.setValue('postOffice', option || { id: '' }, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
          </ListItem>
          <ListItem>
            <Controller
              control={form.control}
              name="effectiveWorkers"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('Effective workers', { defaultValue: 'Effective workers' })}
                  fullWidth
                  disabled={!isAdditionalInfoInEditMode}
                  error={!!form.formState.errors.effectiveWorkers}
                  helperText={form.formState.errors.effectiveWorkers?.message?.toString()}
                />
              )}
            />
          </ListItem>
          <ListItem>
            <Controller
              control={form.control}
              name="employees"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('Employees', { defaultValue: 'Employees' })}
                  fullWidth
                  disabled={!isAdditionalInfoInEditMode}
                  error={!!form.formState.errors.employees}
                  helperText={form.formState.errors.employees?.message?.toString()}
                />
              )}
            />
          </ListItem>
          <ListItem>
            <Controller
              control={form.control}
              name="agencyWorkers"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('Agency workers', { defaultValue: 'Agency workers' })}
                  fullWidth
                  disabled={!isAdditionalInfoInEditMode}
                  error={!!form.formState.errors.agencyWorkers}
                  helperText={form.formState.errors.agencyWorkers?.message?.toString()}
                />
              )}
            />
          </ListItem>
          <ListItem>
            <Controller
              control={form.control}
              name="students"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('Students', { defaultValue: 'Students' })}
                  fullWidth
                  disabled={!isAdditionalInfoInEditMode}
                  error={!!form.formState.errors.students}
                  helperText={form.formState.errors.students?.message?.toString()}
                />
              )}
            />
          </ListItem>
        </List>
        <List
          sx={{
            display: 'grid',
            'grid-template-columns': 'repeat(3, 1fr)',
          }}
        >
          <ListItem>
            <FormControlLabel
              label={t('Locations', { defaultValue: 'Locations' })}
              control={
                <Controller
                  control={form.control}
                  name="locations"
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={!isAdditionalInfoInEditMode}
                      onChange={(e) => {
                        setCommentShow(true);
                        field.onChange(e.target.checked);
                      }}
                    />
                  )}
                />
              }
            />
          </ListItem>
          <ListItem>
            <FormControlLabel
              label={t('Multisite', { defaultValue: 'Multisite' })}
              control={
                <Controller
                  control={form.control}
                  name="multiSite"
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={!isAdditionalInfoInEditMode}
                      onChange={(e) => {
                        setCommentShow(true);
                        field.onChange(e.target.checked);
                      }}
                    />
                  )}
                />
              }
            />
          </ListItem>
          <ListItem>
            <FormControlLabel
              label={t('Temp locations', { defaultValue: 'Temp locations' })}
              control={
                <Controller
                  control={form.control}
                  name="tempLocations"
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={!isAdditionalInfoInEditMode}
                      onChange={(e) => {
                        setCommentShow(true);
                        field.onChange(e.target.checked);
                      }}
                    />
                  )}
                />
              }
            />
          </ListItem>
        </List>
        <List
          sx={{ mt: 5, width: '100%', borderRadius: '4px', boxShadow: 1 }}
          component="div"
          aria-labelledby="offer-list"
          subheader={
            <ListSubheader component="div" id="nested-list-subheader" sx={{ borderRadius: '4px' }}>
              {t('Contacts', { defaultValue: 'Contacts' })}
            </ListSubheader>
          }
        >
          {contactsData.map((contactData, index) => (
            <Box component="div" key={contactData.id}>
              <ListItemButton onClick={() => handleClick(contactData.id)}>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={contactData.name ?? 'N/A'}
                />
                {renderExpandButton(contactData)}
              </ListItemButton>

              {form.getValues().contacts.length > 0 ? (
                <Collapse
                  in={contactData.open}
                  timeout="auto"
                  unmountOnExit
                  key={`${contactData.id}}`}
                >
                  <List component="div" dense disablePadding>
                    <ListItemButton
                      sx={{
                        pl: 4,
                        '&.Mui-disabled': {
                          background: '#a3a3a317',
                          opacity: 1,
                          color: '#000000',
                        },
                      }}
                      disabled={!isAdditionalInfoInEditMode}
                      onClick={() => {
                        setIndexContact(index);
                        openModal();
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: '1.25rem',
                        }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Name', { defaultValue: 'Name' })}
                        secondary={contactData.name ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Mail', { defaultValue: 'Mail' })}
                        secondary={contactData.mail ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Phone', { defaultValue: 'Phone' })}
                        secondary={contactData.tel ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Mobile', { defaultValue: 'Mobile' })}
                        secondary={contactData.mobile ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Role', { defaultValue: 'Role' })}
                        secondary={
                          contactData.role
                            ? t(contactData.role, { defaultValue: contactData.role })
                            : ''
                        }
                      />
                    </ListItemButton>
                  </List>
                </Collapse>
              ) : null}
            </Box>
          ))}
        </List>

        {commentShow && (
          <Controller
            control={form.control}
            name="comment"
            render={({ field }) => (
              <TextField
                {...field}
                type="text"
                label={t('Comment', { defaultValue: 'Comment' })}
                fullWidth
                error={!!form.formState.errors.comment}
                helperText={form.formState.errors.comment?.message?.toString()}
              />
            )}
          />
        )}

        {isAdditionalInfoInEditMode && (
          <>
            <Button
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                form.handleSubmit(baseSubmit)();
              }}
              fullWidth
            >
              {t('Submit changes', { defaultValue: 'Submit changes' })}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setIsAdditionalInfoInEditMode(false);
                form.reset({
                  title: auditorVisit.workOrder?.title,
                  address: auditorVisit.workOrder?.address,
                  postOffice: auditorVisit.workOrder?.postOffice,
                  effectiveWorkers: auditorVisit.workOrder?.effectiveWorkers,
                  employees: auditorVisit.workOrder?.employees,
                  agencyWorkers: auditorVisit.workOrder?.agencyWorkers,
                  students: auditorVisit.workOrder?.students,
                  locations: auditorVisit.workOrder?.locations,
                  multiSite: auditorVisit.workOrder?.multiSite,
                  tempLocations: auditorVisit.workOrder?.tempLocations,
                  comment: auditorVisit.workOrder?.comment,
                  contacts: auditorVisit.workOrder?.contacts,
                });
                setContactsData(auditorVisit.workOrder?.contacts || []);
                setCommentShow(false);
              }}
            >
              {t('Cancel', { defaultValue: 'Cancel' })}
            </Button>
          </>
        )}

        {!!form.formState.errors.root && (
          <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
        )}
      </Box>
    </Box>
  );
};

export default Customer;
